import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import {Box, CardHeader, Typography} from "@mui/material";
import { toSlots } from "../shared/class/event.ts";
import { display24HourTime, dateToHumanDateStr, dateStrToDateObj } from "../shared/date.js";
import { EditTitle } from "./EditTitle.jsx";
import { EditUrl } from "./EditUrl.jsx";
import { PopularButton } from "./Button/PopularButton.jsx";
import SpecialButton from "./Button/SpecialButton.jsx";
import FreeButton from "./Button/FreeButton.jsx";
import VisibilityButton from "./Button/VisibilityButton.jsx";

const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const EventRow = (props) => {
    const { event, onSave } = props;
    const slots = toSlots(event.event_artists || [], event.event_instructors || []);

    return (
        <Box sx={{ mb: 2, p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" color="text.secondary">
                {event.venue?.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1">
                    <strong>{event.title || "Untitled"}</strong>
                </Typography>
                <EditTitle
                    title={event.title}
                    documentId={event.documentId || event.id}
                    onSave={onSave}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <PopularButton event={event} onSave={onSave} />
                <SpecialButton event={event} onSave={onSave} />
                <FreeButton event={event} onSave={onSave} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <EditUrl label="Instagram URL" field="instagramUrl" value={event.instagramUrl}
                         documentId={event.documentId || event.id} onSave={onSave} />
                <EditUrl label="Ticket URL" field="ticketUrl" value={event.ticketUrl}
                         documentId={event.documentId || event.id} onSave={onSave} />
            </Box>

            <Box p={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '6rem 8rem 1fr 8rem 8rem 8rem', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary"><strong>Time</strong></Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Type</strong></Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Title</strong></Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Source</strong></Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Last Seen</strong></Typography>
                    <Typography variant="body2" color="text.secondary"><strong>Listing</strong></Typography>
                </Box>
                {slots.map((slot, i) => (
                    <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '6rem 8rem 1fr 8rem 8rem 8rem', gap: 1 }}>
                        <Typography variant="body2">{slot.time ? display24HourTime(slot.time) : ""}</Typography>
                        <Typography variant="body2">{slot.slotType}</Typography>
                        <Typography variant="body2">{slot.title}</Typography>
                        <Typography variant="body2">{slot.source || ""}</Typography>
                        <Typography variant="body2">{slot.lastSeen || ""}</Typography>
                        <VisibilityButton slot={slot} onSave={onSave} />
                    </Box>
                ))}
            </Box>
        </Box>
    );
};


export const AdminCalendarPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadCalendar = () => {
        return fetch(`${BACKEND_HOST}/events/calendar`)
            .then(res => res.json())
            .then(data => {
                const sorted = data.sort((a, b) => {
                    const dateCompare = (a.date || "").localeCompare(b.date || "");
                    if (dateCompare !== 0) return dateCompare;
                    return (a.venue?.name || "").localeCompare(b.venue?.name || "");
                });
                setEvents(sorted);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading calendar:', error);
                setLoading(false);
            });
    }

    useEffect(() => {
        loadCalendar();
    }, [])

    const eventsByDate = events.reduce((acc, event) => {
        const date = event.date || "Unknown";
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
    }, {});

    return (
        <Card>
            <CardHeader title={"Calendar Administration"} />
            <CardContent>
                {loading ? (
                    <Typography variant="body2">Loading...</Typography>
                ) : (
                    Object.entries(eventsByDate).map(([date, dayEvents]) => (
                        <Box key={date}>
                            <Box sx={{
                                zIndex: 1,
                                bgcolor: 'background.paper',
                                py: 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                paddingLeft: 2,
                            }}>
                                <Typography variant="h6">{dateToHumanDateStr(dateStrToDateObj(date))}</Typography>
                            </Box>
                            {dayEvents.map((event, i) => (
                                <EventRow key={event.documentId || event.id || i} event={event} onSave={loadCalendar} />
                            ))}
                        </Box>
                    ))
                )}
            </CardContent>
        </Card>
    );
};
